import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { operations, selectors } from 'store/curriculumNavigationManager'
import { AppToContentGroupCode } from 'utils/routing/routeProductMaps'
import { isFlagOn, SUBNAV_BREADCRUMBS_FLAG } from 'utils/featureFlags'
import {
  getActiveSiteIdKeyForProgramType,
  getProgramType,
} from 'layers/content/v2/Hocs/withSiteContext/helpers'

const emptySelection = { data: null, error: null, loading: false }

/**
 * Whether the program subnav for a route-service app should come from the
 * curriculum service GET /v1/navigation instead of route-service.
 */
export function shouldUseCurriculumNavigation(app) {
  return !!AppToContentGroupCode[app] && isFlagOn([SUBNAV_BREADCRUMBS_FLAG])
}

const selectActiveSiteId = state => {
  const activeSiteIdKey = getActiveSiteIdKeyForProgramType(getProgramType())
  return activeSiteIdKey
    ? state.userContextManager?.[activeSiteIdKey] ?? null
    : null
}

/**
 * Fetches the program subnav from the curriculum service for the active site
 * of the current program. Does nothing until an active site id is available.
 *
 * @param {Object} options
 * @param {string} options.app - Route-service app key (e.g. RouteContentProductAppMap.learnMsProduct)
 * @param {boolean} options.enabled - When false the hook neither selects nor fetches
 */
export function useCurriculumNavigation({ app, enabled }) {
  const dispatch = useDispatch()
  const contentGroupCode = enabled ? AppToContentGroupCode[app] : null
  const siteId = useSelector(state =>
    contentGroupCode ? selectActiveSiteId(state) : null,
  )
  const hasDeps = !!contentGroupCode && siteId !== null

  const { data, error, loading } = useSelector(
    state =>
      hasDeps
        ? selectors.selectCurriculumNavigation(contentGroupCode, siteId)(state)
        : emptySelection,
    shallowEqual,
  )

  // A cached error is retried on the next mount (Navigation remounts per route)
  // so one failed request does not remove the subnav until a full reload.
  useEffect(() => {
    if (!hasDeps || data || loading) return

    dispatch(operations.fetchCurriculumNavigation({ contentGroupCode, siteId }))
  }, [contentGroupCode, siteId])

  // Also loading on the render before the fetch effect dispatches, so callers
  // never see a settled-but-empty frame.
  const isAwaitingFetch = hasDeps && !data && !error

  return { data, error, loading: loading || isAwaitingFetch }
}
